import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { seoArticles } from '@/mocks/siteData';

export default function ShoppingGuidePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Shopping Guide - itaobuy.living',
      description: 'Complete itaobuy spreadsheet shopping guide with expert articles, product sourcing tips, and cross-border buying strategies.',
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <header className="pt-24 pb-12 md:pb-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <nav className="flex items-center justify-center gap-2 text-sm text-foreground-500 mb-6" aria-label="breadcrumb">
              <Link to="/" className="hover:text-foreground-950 transition-colors whitespace-nowrap">Home</Link>
              <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center"></i>
              <span className="text-foreground-950 font-medium">Shopping Guide</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
              Itaobuy Shopping Guide
            </h1>
            <p className="mt-4 text-foreground-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Everything you need to know about the <strong>itaobuy spreadsheet</strong> system — from getting started to advanced sourcing strategies. Browse our complete collection of expert guides below.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                Back to Home
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                BUY IT NOW
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </header>

        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                All Sourcing Guides
              </h2>
              <p className="mt-3 text-foreground-600 text-base max-w-lg mx-auto">
                {seoArticles.length} expert articles covering every aspect of the itaobuy spreadsheet system
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {seoArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group block rounded-2xl border border-background-200/70 bg-background-50 overflow-hidden hover:border-foreground-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[3/2] overflow-hidden bg-background-100">
                    <img
                      src={article.image}
                      alt={`Itaobuy spreadsheet article - ${article.title}`}
                      title={`Itaobuy Spreadsheet Guide - ${article.title}`}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 md:p-6">
                    <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-primary-600 group-hover:text-primary-700 transition-colors whitespace-nowrap">
                      Read Article
                      <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
              Ready to Start Sourcing?
            </h2>
            <p className="mt-4 text-foreground-600 text-base max-w-lg mx-auto">
              Put your knowledge into action. Browse curated product categories and start sourcing smarter with the <strong>itaobuy spreadsheet</strong> system today.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                ITAOBUY SHEET
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}